import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, resource, signal } from '@angular/core';
import { Pipe, PipeTransform } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import moment from 'moment';
import { AgreementService } from 'src/app/service';

import { Agreement } from '../schemas';

@Pipe({
  name: 'ordinal',
  standalone: true,
})
export class OrdinalPipe implements PipeTransform {
  transform(n: number): string {
    if (isNaN(n) || n < 1) {
      return String(n); // Or handle as needed
    }
    const s = String(n);
    const lastTwoDigits = n % 100;
    // Special case for 11, 12, 13 - they always use "th"
    if (lastTwoDigits === 11 || lastTwoDigits === 12 || lastTwoDigits === 13) {
      return s + 'th';
    }
    const lastDigit = n % 10;
    switch (lastDigit) {
      case 1:
        return s + 'st';
      case 2:
        return s + 'nd';
      case 3:
        return s + 'rd';
      default:
        return s + 'th';
    }
  }
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './invoices-page.html',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatToolbarModule,
    MatTableModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,

    OrdinalPipe,
  ],
})
export class InvoicesPage {
  private agreementService = inject(AgreementService);

  StartDate = signal(moment());
  EndDate = signal(moment().add(1, 'year'));

  readonly minDate = moment();
  readonly maxDate = moment().add(3, 'years');

  expanded = signal<Record<string, boolean>>({});
  expand(key: string) {
    this.expanded.update((data) => ({ ...data, [key]: !data[key] }));
  }

  readonly columnsToDisplay = [
    'ClientCode',
    'ClientName',
    'AgreementNumber',
    'Service',
    'BillingCycle',
    'Expiry',
    'Amount',
  ];

  pending = this.agreementService.pending;

  toBeInvoicedSum = signal(0);
  overdueInvoicesSum = signal(0);

  invoicesResource = resource({
    params: () => ({ start: this.StartDate(), end: this.EndDate(), agreements: this.agreementService.agreements() }),
    defaultValue: [],
    loader: async ({ params }) => {
      this.toBeInvoicedSum.set(0)
      this.overdueInvoicesSum.set(0)
      const { start, end } = params;
      if (!start || !end) {
        // prevent requests when end date is missing or before the start date
        return [];
      }

      // compute range from the midnight instead of the current time
      const dueWindow = end.startOf('day').diff(moment().startOf('day'), 'day');

      const now = new Date();

      const invoices = params.agreements.filter((it) => {
        const invoiceDate = it.NextInvoiceDate ? new Date(it.NextInvoiceDate) : now;
        const diff = invoiceDate.getTime() - now.getTime();

        const invoiceIn = Math.ceil(diff / 1000 / 3600 / 24);

        return (
          it.Status !== 'Expired' &&
          invoiceIn <= dueWindow &&
          invoiceIn >= (start.diff(moment().startOf('day'), 'days') || -Infinity)
        );
      });

      invoices.forEach((it) => {
        const invoiceDate = it.NextInvoiceDate ? new Date(it.NextInvoiceDate) : now;
        const diff = invoiceDate.getTime() - now.getTime();
        const invoiceIn = Math.ceil(diff / 1000 / 3600 / 24);

        // Calculate invoice occurrences based on billing cycle
        const { single, occurrences, dates } = this.calculateInvoices(it, end);
        const value = single * occurrences;
        this.toBeInvoicedSum.update((v) => v + value);
        it['_composition'] = dates;
        it['_invoiceSum'] = value;
        it['_invoiceSingle'] = single;
        it['_invoiceIn'] = invoiceIn;

        // Track overdue invoices (InvoiceIn < 0 means past due)
        if (invoiceIn! < 0) {
          this.overdueInvoicesSum.update((v) => v + single);
        }
      });
      return invoices;
    },
  });

  invoices = computed(() => this.invoicesResource.value());

  trackBy(idx: number, row: Agreement) {
    return row.AgreementNumber;
  }

  reload() {
    this.agreementService.reload();
  }

  calculateInvoices(
    invoice: Agreement,
    endDate: moment.Moment,
  ): { single: number; occurrences: number; dates: moment.Moment[] } {
    // Sum of service, hosting, and support maintenance amounts
    const single =
      (invoice.ServiceAmount || 0) + (invoice.HostingAmount || 0) + (invoice.SupportMaintenanceAmount || 0);
    // Map billing cycle to month duration
    const duration = {
      Monthly: 1,
      Quarterly: 3,
      'Bi-annually': 6,
      Yearly: 12,
    }[invoice.BillingCycle];
    const nextInvoice = moment(invoice.NextInvoiceDate);
    const dates = [nextInvoice];
    if (!endDate || !duration) {
      return { single, occurrences: 1, dates };
    }

    // Calculate how many billing cycles fit between next invoice date and end date
    const months = endDate.diff(nextInvoice, 'months');
    const occurrences = Math.floor(months / duration) + 1;
    // Generate all invoice dates in the range
    for (let i = 1; i < occurrences; i++) {
      const point = nextInvoice.clone();
      point.add(duration * i, 'months');
      dates.push(point);
    }
    return { single, occurrences, dates };
  }
}
